import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, CheckCircle, Circle, Save } from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from '@/services/api';
import changePasswordIcon from '@/assets/change-password-icon.png';

const INPUT_CLASS =
  "w-full px-3 py-2.5 border-0 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 transition-colors bg-[#F6F9FB]";

const PwdField = ({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className={`${INPUT_CLASS} pr-10`}
          placeholder="••••••"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const Rule = ({ label, met }: { label: string; met: boolean }) => (
  <div className="flex items-center gap-1.5">
    {met ? (
      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
    ) : (
      <Circle className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
    )}
    <span className={`text-xs ${met ? "text-green-600" : "text-gray-400"}`}>{label}</span>
  </div>
);

// Una sola pantalla, no un wizard de pasos: el back exige currentPassword +
// newPassword juntas en la misma llamada (no hay forma de validar solo la
// actual por separado), así que los 3 campos se piden y se mandan de una vez.
export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [saving, setSaving] = useState(false);

  const rules = [
    { label: "Al menos 8 caracteres", met: nueva.length >= 8 },
    { label: "Letra minúscula", met: /[a-z]/.test(nueva) },
    { label: "Letra mayúscula", met: /[A-Z]/.test(nueva) },
    { label: "Número", met: /\d/.test(nueva) },
    { label: "Carácter especial", met: /[^a-zA-Z0-9]/.test(nueva) },
  ];
  const allRulesMet = rules.every((r) => r.met);

  const handleGuardar = async () => {
    if (saving) return;
    if (!actual) {
      toast.error("Ingresa tu contraseña actual.");
      return;
    }
    if (!allRulesMet) {
      toast.error("La nueva contraseña no cumple todos los requisitos.");
      return;
    }
    if (nueva !== confirmar) {
      toast.error("Las contraseñas no coinciden.");
      return;
    }
    setSaving(true);
    try {
      // El back invalida el resto de sesiones (bump de tokenVersion) y reemite
      // un access_token nuevo — hay que guardarlo de inmediato o la siguiente
      // request usa el token viejo (ya invalido) y esta sesión queda deslogueada.
      const response = await apiService.changePassword({
        currentPassword: actual,
        newPassword: nueva,
      });
      apiService.saveToken(response.access_token);
      toast.success("Contraseña actualizada correctamente.");
      navigate(-1);
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string | string[] };
      const msg = Array.isArray(e.message) ? e.message.join(" ") : e.message;
      if (e.statusCode === 400) {
        // Contraseña actual incorrecta — se queda en esta misma pantalla para reintentar.
        toast.error(msg ?? "La contraseña actual es incorrecta.");
        setActual("");
      } else {
        toast.error(msg ?? "No se pudo actualizar la contraseña.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={estiloDeBoton("secundario")}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cambiar contraseña</h1>
          <p className="text-sm text-gray-500 mt-1">Actualiza la contraseña de tu cuenta</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
        <div className="flex justify-center mb-6">
          <img src={changePasswordIcon} alt="" className="w-32 h-auto" />
        </div>

        <div className="max-w-sm mx-auto space-y-5">
          <PwdField label="Contraseña actual" value={actual} onChange={setActual} autoComplete="current-password" />
          <PwdField label="Nueva contraseña" value={nueva} onChange={setNueva} autoComplete="new-password" />
          <PwdField label="Confirmar contraseña" value={confirmar} onChange={setConfirmar} autoComplete="new-password" />

          <div className="bg-[#F0FAFD] rounded-lg p-4 grid grid-cols-2 gap-2">
            {rules.map((r) => (
              <Rule key={r.label} label={r.label} met={r.met} />
            ))}
          </div>

          <button
            type="button"
            onClick={handleGuardar}
            disabled={saving}
            className={estiloDeBoton("primario", "w-full")}
          >
            <Save className="w-4 h-4" />
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
